import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faCog, faUser, faShield, faSave, faSpinner, faCheckCircle,
  faEnvelope, faLock, faEye, faEyeSlash, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const isRTL = i18n.language === 'ar';
  const rtlDir = isRTL ? 'rtl' : 'ltr';

  const [profileData, setProfileData] = useState({
    fullName: '',
    email: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName || '',
        email: user.email || ''
      });
      fetchAccountInfo();
    }
  }, [user]);

  const fetchAccountInfo = async () => {
    try {
      const response = await api.get('/auth/me');
      setAccountInfo(response.data.user);
    } catch (error) {
      console.error('Failed to fetch account info:', error);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess(false);
    setProfileLoading(true);

    try {
      const response = await api.put('/auth/profile', {
        fullName: profileData.fullName.trim() || null,
        email: profileData.email.trim()
      });

      const updatedUser = {
        ...user!,
        fullName: response.data.user.full_name,
        email: response.data.user.email
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err: any) {
      setProfileError(err.response?.data?.error || t('settings.failedToUpdateProfile'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (passwordData.newPassword.length < 6) {
      setPasswordError(t('settings.passwordMinLength'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('settings.passwordsDoNotMatch'));
      return;
    }

    setPasswordLoading(true);

    try {
      await api.put('/auth/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || t('settings.failedToChangePassword'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const hasPassword = accountInfo?.has_password === true;

  const successAlert = (message: string) => (
    <div
      className={`mb-4 p-3 rounded-md text-sm flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
      style={{ background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)', color: 'var(--gp-success)' }}
    >
      <FontAwesomeIcon icon={faCheckCircle} />
      <p className={isRTL ? 'text-right' : 'text-left'}>{message}</p>
    </div>
  );

  return (
    <div className="gp-page py-6" dir={rtlDir}>
      <div className="gp-container" style={{ maxWidth: '48rem' }}>
        <Link
          to="/dashboard"
          className={`gp-back-link mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
          {t('common.backToDashboard')}
        </Link>

        <div className={`gp-page-header ${isRTL ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="gp-icon-tile">
              <FontAwesomeIcon icon={faCog} />
            </div>
            <h1 className="gp-page-title">{t('settings.title')}</h1>
          </div>
          <p className="gp-page-subtitle">{t('settings.description')}</p>
        </div>

        <div className="gp-card mb-4">
          <div
            className={`flex items-center gap-3 mb-4 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}
            style={{ borderBottom: '1px solid var(--gp-border)' }}
          >
            <FontAwesomeIcon icon={faUser} style={{ color: 'var(--gp-brand)' }} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="font-display text-lg font-semibold" style={{ color: 'var(--gp-ink)' }}>
                {t('settings.profileSettings')}
              </h2>
              <p className="text-xs gp-text-muted">{t('settings.updatePersonalInfo')}</p>
            </div>
          </div>

          {profileSuccess && successAlert(t('settings.profileUpdatedSuccess'))}
          {profileError && (
            <div className={`gp-alert-error flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <p className={isRTL ? 'text-right' : 'text-left'}>{profileError}</p>
            </div>
          )}

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="fullName"
                className={`block text-sm font-medium mb-2 ${isRTL ? 'text-right' : 'text-left'}`}
                style={{ color: 'var(--gp-ink)' }}
              >
                <FontAwesomeIcon icon={faUser} className={isRTL ? 'ml-2' : 'mr-2'} />
                {t('settings.fullName')}
              </label>
              <input
                type="text"
                id="fullName"
                value={profileData.fullName}
                onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                placeholder={t('settings.fullNamePlaceholder')}
                className={`input-field ${isRTL ? 'text-right' : 'text-left'}`}
                dir={rtlDir}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-2 ${isRTL ? 'text-right' : 'text-left'}`}
                style={{ color: 'var(--gp-ink)' }}
              >
                <FontAwesomeIcon icon={faEnvelope} className={isRTL ? 'ml-2' : 'mr-2'} />
                {t('settings.emailAddress')}
              </label>
              <input
                type="email"
                id="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                required
                placeholder={t('settings.emailPlaceholder')}
                className={`input-field ${isRTL ? 'text-right' : 'text-left'}`}
                dir={rtlDir}
              />
            </div>

            <div className={`flex pt-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <button
                type="submit"
                disabled={profileLoading}
                className={`gp-btn-primary ${isRTL ? 'flex-row-reverse' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {profileLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>{t('settings.saving')}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    <span>{t('settings.save')}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {hasPassword && (
          <div className="gp-card mb-4">
            <div
              className={`flex items-center gap-3 mb-4 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}
              style={{ borderBottom: '1px solid var(--gp-border)' }}
            >
              <FontAwesomeIcon icon={faShield} style={{ color: 'var(--gp-brand)' }} />
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <h2 className="font-display text-lg font-semibold" style={{ color: 'var(--gp-ink)' }}>
                  {t('settings.changePassword')}
                </h2>
                <p className="text-xs gp-text-muted">{t('settings.updateAccountPassword')}</p>
              </div>
            </div>

            {passwordSuccess && successAlert(t('settings.passwordChangedSuccess'))}
            {passwordError && (
              <div className={`gp-alert-error flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p className={isRTL ? 'text-right' : 'text-left'}>{passwordError}</p>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="currentPassword"
                  className={`block text-sm font-medium mb-2 ${isRTL ? 'text-right' : 'text-left'}`}
                  style={{ color: 'var(--gp-ink)' }}
                >
                  <FontAwesomeIcon icon={faLock} className={isRTL ? 'ml-2' : 'mr-2'} />
                  {t('settings.currentPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    placeholder={t('settings.currentPasswordPlaceholder')}
                    className={`input-field ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'}`}
                    dir={rtlDir}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 gp-text-muted`}
                  >
                    <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="newPassword"
                  className={`block text-sm font-medium mb-2 ${isRTL ? 'text-right' : 'text-left'}`}
                  style={{ color: 'var(--gp-ink)' }}
                >
                  <FontAwesomeIcon icon={faLock} className={isRTL ? 'ml-2' : 'mr-2'} />
                  {t('settings.newPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                    placeholder={t('settings.newPasswordPlaceholder')}
                    className={`input-field ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'}`}
                    dir={rtlDir}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 gp-text-muted`}
                  >
                    <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className={`block text-sm font-medium mb-2 ${isRTL ? 'text-right' : 'text-left'}`}
                  style={{ color: 'var(--gp-ink)' }}
                >
                  <FontAwesomeIcon icon={faLock} className={isRTL ? 'ml-2' : 'mr-2'} />
                  {t('settings.confirmNewPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                    placeholder={t('settings.confirmPasswordPlaceholder')}
                    className={`input-field ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'}`}
                    dir={rtlDir}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 gp-text-muted`}
                  >
                    <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className={`flex pt-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className={`gp-btn-primary ${isRTL ? 'flex-row-reverse' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {passwordLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>{t('settings.changingPassword')}</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} />
                      <span>{t('settings.changePassword')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="gp-card">
          <div
            className={`flex items-center gap-3 mb-4 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}
            style={{ borderBottom: '1px solid var(--gp-border)' }}
          >
            <FontAwesomeIcon icon={faUser} style={{ color: 'var(--gp-brand)' }} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="font-display text-lg font-semibold" style={{ color: 'var(--gp-ink)' }}>
                {t('settings.accountInformation')}
              </h2>
              <p className="text-xs gp-text-muted">{t('settings.viewAccountDetails')}</p>
            </div>
          </div>

          {loadingInfo ? (
            <div className="flex items-center justify-center py-8">
              <div className="gp-spinner" />
            </div>
          ) : accountInfo ? (
            <div className="space-y-1">
              {[
                { label: t('common.email'), value: accountInfo.email },
                { label: t('settings.fullName'), value: accountInfo.full_name || t('settings.notSet') },
                { label: t('settings.accountCreated'), value: formatDate(accountInfo.created_at) },
                {
                  label: t('settings.accountType'),
                  value: hasPassword
                    ? t('settings.emailPassword')
                    : accountInfo?.social_provider
                    ? `${accountInfo.social_provider.charAt(0).toUpperCase() + accountInfo.social_provider.slice(1)} ${t('settings.oauth')}`
                    : t('settings.socialLogin'),
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between items-center py-2.5`}
                  style={{ borderBottom: '1px solid var(--gp-border)' }}
                >
                  <span className={`text-sm font-medium gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                    {row.label}
                  </span>
                  <span className={`text-sm ${isRTL ? 'text-right' : 'text-left'}`} style={{ color: 'var(--gp-ink)' }}>
                    {row.value}
                  </span>
                </div>
              ))}
              {accountInfo?.social_provider && (
                <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between items-center py-2.5`}>
                  <span className={`text-sm font-medium gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('settings.loginMethod')}
                  </span>
                  <span className={`text-sm capitalize ${isRTL ? 'text-right' : 'text-left'}`} style={{ color: 'var(--gp-ink)' }}>
                    {accountInfo.social_provider}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className={`text-sm gp-text-muted ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('settings.failedToLoadAccountInfo')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
